import { FunctionComponent, useState, useRef } from "react";
import { useAppDispatch } from "./store/hooks";
import { setUserProfile } from "./store/userSlice";
import { login, register, resetPassword } from "./services/AuthService";
import { getUserProfile, createUserProfile, isNicknameAvailable } from "./services/FirebaseUsers";
import { sendWelcomeEmail } from "./services/EmailService";

type Mode = "login" | "register" | "reset";

const AuthScreen: FunctionComponent = () => {
  const dispatch = useAppDispatch();

  const [mode, setMode]         = useState<Mode>("login");
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [nickname, setNickname] = useState("");
  const [error, setError]       = useState("");
  const [info, setInfo]         = useState("");
  const [loading, setLoading]   = useState(false);

  const emailRef = useRef<HTMLInputElement>(null);

  const clear = () => { setError(""); setInfo(""); };
  const switchMode = (m: Mode) => { setMode(m); clear(); };

  const handleSubmit = async () => {
    clear();
    if (!email.trim()) { setError("Введите email"); return; }

    if (mode === "reset") {
      setLoading(true);
      const r = await resetPassword(email.trim());
      setLoading(false);
      if (r.ok) setInfo("Письмо отправлено. Проверьте почту.");
      else setError(r.error ?? "Ошибка");
      return;
    }

    if (!password) { setError("Введите пароль"); return; }

    if (mode === "register") {
      const nick = nickname.trim();
      if (nick.length < 3) { setError("Никнейм: минимум 3 символа"); return; }
      setLoading(true);
      const avail = await isNicknameAvailable(nick);
      if (!avail) { setError("Никнейм уже занят"); setLoading(false); return; }
      const r = await register(email.trim(), password, nick);
      setLoading(false);
      if (!r.ok) { setError(r.error ?? "Ошибка"); return; }
      const profile = await getUserProfile(r.uid!);
      if (profile) dispatch(setUserProfile(profile));
      else { const p = await createUserProfile(r.uid!, nick); dispatch(setUserProfile(p)); }
      sendWelcomeEmail(email.trim(), nick);
      return;
    }

    // login
    setLoading(true);
    const r = await login(email.trim(), password);
    if (!r.ok) { setError(r.error ?? "Ошибка"); setLoading(false); return; }
    const profile = await getUserProfile(r.uid!);
    setLoading(false);
    if (profile) {
      dispatch(setUserProfile(profile));
    } else {
      const nick = email.split("@")[0] || "Игрок";
      const p = await createUserProfile(r.uid!, nick);
      dispatch(setUserProfile(p));
    }
  };

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !loading) handleSubmit();
  };

  const submitLabel =
    mode === "login"    ? "Войти" :
    mode === "register" ? "Зарегистрироваться" :
    "Отправить письмо";

  return (
    <div className="Auth-screen">
      <div className="Auth-card">
        <h1 className="Auth-title">Нарды</h1>

        {mode !== "reset" && (
          <div className="Auth-tabs">
            <button
              className={`Auth-tab ${mode === "login" ? "active" : ""}`}
              onClick={() => switchMode("login")}
            >Войти</button>
            <button
              className={`Auth-tab ${mode === "register" ? "active" : ""}`}
              onClick={() => switchMode("register")}
            >Регистрация</button>
          </div>
        )}
        {mode === "reset" && <p className="Auth-subtitle">Сброс пароля</p>}

        <div className="Auth-form">
          {mode === "register" && (
            <input
              className="Auth-input"
              type="text"
              placeholder="Никнейм"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              onKeyDown={handleKey}
              maxLength={20}
              autoFocus
              autoComplete="username"
            />
          )}
          <input
            ref={emailRef}
            className="Auth-input"
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={handleKey}
            autoComplete="email"
            autoFocus={mode === "login"}
          />
          {mode !== "reset" && (
            <input
              className="Auth-input"
              type="password"
              placeholder={mode === "register" ? "Пароль (мин. 6 символов)" : "Пароль"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={handleKey}
              autoComplete={mode === "register" ? "new-password" : "current-password"}
            />
          )}

          {error && <div className="Auth-error">{error}</div>}
          {info  && <div className="Auth-info">{info}</div>}

          <button className="Auth-submit" onClick={handleSubmit} disabled={loading}>
            {loading ? <span className="Btn-spinner" /> : submitLabel}
          </button>

          {mode === "login" && (
            <button className="Auth-link" onClick={() => switchMode("reset")}>
              Забыли пароль?
            </button>
          )}
          {mode === "reset" && (
            <button className="Auth-link" onClick={() => switchMode("login")}>
              ← Назад
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthScreen;
